import { createSelector } from "reselect";

import { RootState } from "../../store/types";
import { chosenProduct } from "../Step1/selectors";
import { projFormValues } from "../Step4/selectors";
import { suppFormValues } from "../Step5/selectors";
import {
  selectedStyle,
  descriptionValue,
} from "../WizardStyleChoice/selectors";
import { SummaryState } from "./reducer";
import * as constants from "./constants";

export const summary = (state: RootState): SummaryState => state.wizardSummary;

export const isDataLoading = createSelector(
  summary,
  state => state.isDataLoading,
);

export const costPolicy = createSelector(summary, state => state.costPolicy);
export const timePolicies = createSelector(
  summary,
  state => state.timePolicies,
);
export const chargePolicies = createSelector(
  summary,
  state => state.chargePolicies,
);

export const isTOCChecked = createSelector(
  summary,
  state => state.isTOCChecked,
);
export const productName = createSelector(
  chosenProduct,
  product => (product ? product.name : ""),
);
export const chosenStyles = createSelector(
  selectedStyle,
  descriptionValue,
  (selectedStyle, customStyle): string[] => {
    const styles: string[] = [];
    if (selectedStyle) {
      styles.push(selectedStyle.name);
    }
    if (customStyle) {
      styles.push(customStyle);
    }
    return styles;
  },
);

export const sampleNumber = createSelector(
  costPolicy,
  suppFormValues,
  (costPolicy, suppForm) => {
    if (!costPolicy) {
      return "";
    }

    let sampleNumber = suppForm.num_additional_designs
      ? +suppForm.num_additional_designs
      : 0;

    sampleNumber += costPolicy.min_deliverable_number;

    return sampleNumber.toString();
  },
);

export const cost = createSelector(
  costPolicy,
  chargePolicies,
  projFormValues,
  suppFormValues,
  (costPolicy, chargePolicies, projForm, suppForm) => {
    if (!costPolicy) {
      return "";
    }

    const pageNumber = projForm.page_number ? +projForm.page_number : 1;
    const addedDeliverables =
      suppForm.num_additional_designs && +suppForm.num_additional_designs;

    // Base cost calc
    let cost = costPolicy.base_cost * pageNumber;

    // Apply add. deliverables charges
    if (addedDeliverables && addedDeliverables > 0) {
      // Find correct charge policy
      const correctPolicy = chargePolicies.find(
        policy =>
          pageNumber >= policy.min_unit && pageNumber <= policy.max_unit,
      );

      if (correctPolicy) {
        const appliedRate =
          +correctPolicy.charge_per_add_deliverable_rate * addedDeliverables;

        cost *= 1 + appliedRate;
      }
    }

    // Apply spec. req rate
    if (suppForm.special_req && suppForm.special_req.length) {
      cost *= 1 + +costPolicy.charge_special_req_rate;
    }

    // Apply express rate
    if (suppForm.has_express_req === "true") {
      cost *= 1 + +costPolicy.charge_express_req_rate;
    }

    // Apply VAT rate
    if (suppForm.vat === "true") {
      cost *= 1 + +costPolicy.vat_rate;
    }

    // Apply upfront payment discount
    if (
      suppForm.has_payment_upfront === "true" &&
      suppForm.payment_upfront_rate
    ) {
      const rate =
        constants.PAYMENT_UPFRONT_RATE_LOOKUP[suppForm.payment_upfront_rate] ||
        0;

      cost *= 1 - rate;
    }

    // (not good practice to go against currency format standards but design request)
    return `${cost.toLocaleString(constants.LOCALE)} vnđ`;
  },
);

export const time = createSelector(
  costPolicy,
  timePolicies,
  projFormValues,
  suppFormValues,
  (costPolicy, timePolicies, projForm, suppForm) => {
    if (!costPolicy) {
      return "";
    }

    const pageNumber = projForm.page_number ? +projForm.page_number : 1;
    const addedDeliverables =
      suppForm.num_additional_designs && +suppForm.num_additional_designs;

    // Base time calc
    let time = costPolicy.base_time * pageNumber;

    // Apply add. deliverables times
    if (addedDeliverables && addedDeliverables > 0) {
      // Find correct time policy
      const correctPolicy = timePolicies.find(
        policy =>
          addedDeliverables >= policy.min_add_deliverable &&
          addedDeliverables <= policy.max_add_deliverable,
      );

      if (correctPolicy) {
        time *= 1 + +correctPolicy.time_extra_rate;
      }
    }

    // Apply spec. req rate
    if (suppForm.special_req && suppForm.special_req.length) {
      time *= 1 + +costPolicy.time_special_req_rate;
    }

    // Apply express rate
    if (suppForm.has_express_req === "true") {
      time *= 1 - +costPolicy.time_express_req_rate;
    }

    // Format
    const hours = Math.floor(time / 60);
    const mins = Math.floor(time % 60);

    return `${hours} giờ ${mins} phút`;
  },
);



// WEBPACK FOOTER //
// ./src/routes/WizardSummary/selectors.ts